/**
 * The window every windowed read shares.
 *
 * Shared rather than owned by `campaigns`, because the counters above the leads
 * table and the table itself must ask for the *same* window — otherwise the
 * numbers describe one period and the rows another, on one screen.
 */
export const STATS_PERIODS = ['24h', '7d', '30d'] as const

export type StatsPeriod = (typeof STATS_PERIODS)[number]

export type StatsGranularity = 'hour' | 'day'

const HOUR = 60 * 60 * 1000

/**
 * Granularity is chosen so a chart never asks for more bars than it can draw:
 * a week of hourly buckets is 168 columns in a card a few hundred pixels wide,
 * which renders as an empty box. Twenty-four, seven and thirty all fit.
 */
const SPANS: Record<StatsPeriod, { ms: number; granularity: StatsGranularity }> = {
  '24h': { ms: 24 * HOUR, granularity: 'hour' },
  '7d': { ms: 7 * 24 * HOUR, granularity: 'day' },
  '30d': { ms: 30 * 24 * HOUR, granularity: 'day' },
}

/**
 * The window a period names, with its end rounded up to the hour.
 *
 * The rounding is what makes this safe to call during render: an unrounded
 * `now` would differ on every pass, and since the window is part of the query
 * key that would be a refetch loop. Rounded, the key changes once an hour —
 * which is also exactly how often the rollup gains a new bucket.
 */
export function statsWindow(period: StatsPeriod, now = Date.now()) {
  const span = SPANS[period]
  const to = Math.ceil(now / HOUR) * HOUR

  return {
    from: new Date(to - span.ms).toISOString(),
    to: new Date(to).toISOString(),
    granularity: span.granularity,
  }
}
